import { useCallback, useEffect, useRef, useState } from "react";
import {
  EditorHistoryEngine,
  type EditorCommitOptions,
} from "@/lib/editorHistory/editorHistory";

export type { EditorCommitOptions };

export function useEditorHistory<T extends object>(initialDoc: T) {
  const engineRef = useRef<EditorHistoryEngine<T> | null>(null);
  if (!engineRef.current) {
    engineRef.current = new EditorHistoryEngine(initialDoc);
  }
  const engine = engineRef.current;
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    const onFocusOut = () => {
      engine.flushCoalesce();
    };
    document.addEventListener("focusout", onFocusOut, true);
    return () => {
      document.removeEventListener("focusout", onFocusOut, true);
    };
  }, [engine]);

  const commitDoc = useCallback(
    (producer: (prev: T) => T, options?: EditorCommitOptions) => {
      const next = producer(engine.present);
      engine.commit(next, options);
      bump();
      return engine.present;
    },
    [bump, engine],
  );

  const undo = useCallback(() => {
    const next = engine.undo();
    if (next) bump();
    return next;
  }, [bump, engine]);

  const redo = useCallback(() => {
    const next = engine.redo();
    if (next) bump();
    return next;
  }, [bump, engine]);

  const reset = useCallback(
    (next: T, options?: { markDirty?: boolean }) => {
      engine.reset(next, options);
      bump();
      return engine.present;
    },
    [bump, engine],
  );

  const rebaseline = useCallback(
    (next?: T) => {
      engine.rebaseline(next);
      bump();
    },
    [bump, engine],
  );

  const replacePresent = useCallback(
    (next: T) => {
      engine.replacePresent(next);
      bump();
      return engine.present;
    },
    [bump, engine],
  );

  const flushCoalesce = useCallback(() => {
    engine.flushCoalesce();
  }, [engine]);

  return {
    present: engine.present,
    commitDoc,
    undo,
    redo,
    reset,
    rebaseline,
    replacePresent,
    flushCoalesce,
    canUndo: engine.canUndo,
    canRedo: engine.canRedo,
    hasChanges: engine.hasChanges,
  };
}
